import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLayoutContext } from '../Contexts/LayoutContext';
import { useOrderTypeContext } from '../Contexts/OrderTypeContext';
import CompanyService from '../Services/CompanyService';
import DefaultLayout from '../Layouts/DefaultLayout';
import { ImSpinner8 } from 'react-icons/im';
import { useTranslation } from 'react-i18next';
import { useStoreHook } from '../Hooks/StoreHook';
import DOMPurify from 'dompurify';
import { Breadcrumbs, Link } from '@mui/material';
import { TbHome } from 'react-icons/tb';

const Institutional = () => {
    const { page_slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { 
        company, 
        loadingCompany,
        setMainLoading, 
        setMainLoadingIcon, 
        setMainLoadingIconError, 
        setMainLoadingIconSuccess,
        setMainLoadingTitle, 
        setMainLoadingMessage, 
        setMainLoadingCloseButton 
    } = useLayoutContext();
    const { orderType } = useOrderTypeContext();
    const { loadCompanyInfo, getLinkToHome } = useStoreHook();
    const [htmlContent, setHtmlContent] = useState('');
    const [errorPage, setErrorPage] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pageId, setPageId] = useState<number | null>(null);
    const [title, setTitle] = useState<string>('');
    const containerRef = useRef<HTMLDivElement>(null);

    const companyService = useMemo(() => new CompanyService(orderType), [orderType]);

    useEffect(() => {
        if (!company && !loadingCompany) {
            loadCompanyInfo();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchPage = async () => {
            if (!company || !page_slug) return;
            try {
                setLoading(true);
                const data = await companyService.getPage(page_slug);

                if (data && data.html_content) {
                    setHtmlContent(data.html_content);
                    setPageId(data.id);
                    setTitle(data.title);
                    setErrorPage(false);
                    document.title = `${data.title} - ${company?.name || ''}`;
                } else {
                    setHtmlContent('<p class="flex items-center justify-center h-full text-center text-gray-500">Conteúdo não encontrado.</p>');
                    setErrorPage(true);
                }
            } catch (error) {
                setHtmlContent('<p class="flex items-center justify-center h-full text-center text-gray-500">Erro ao carregar o conteúdo.</p>');
                setErrorPage(true);
            } finally {
                setLoading(false);
            }
        };
        fetchPage();
    }, [page_slug, companyService, company]);

    useEffect(() => {
        if (!containerRef.current || !htmlContent) return;

        const container = containerRef.current;

        // Phone mask
        const cellPhoneInput = container.querySelector('#cellPhone') as HTMLInputElement;
        const handlePhoneMask = (e: any) => {
            let x = e.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,5})(\d{0,4})/);
            e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');
        };
        if (cellPhoneInput) {
            cellPhoneInput.addEventListener('input', handlePhoneMask);
        }

        // Date/Time mask
        const formDataHora = container.querySelector('#FormDataHora') as HTMLInputElement;
        const handleDateMask = (e: any) => {
            let x = e.target.value.replace(/\D/g, '').match(/(\d{0,2})(\d{0,2})(\d{0,2})(\d{0,2})(\d{0,2})/);
            e.target.value = !x[2] ? x[1] : x[1] + '/' + (!x[3] ? x[2] : x[2] + '/') + (!x[4] ? x[3] : x[3] + ' ' + (!x[5] ? x[4] : x[4] + ':' + x[5]));
        };
        if (formDataHora) {
            formDataHora.addEventListener('input', handleDateMask);
        }

        // Subject change
        const subject = container.querySelector('#form_subject') as HTMLSelectElement;
        const subjectOptions = container.querySelector('#ContactSubjectOptions') as HTMLElement;
        const handleSubjectChange = () => {
            if (subject.value === 'Reclamação') {
                if (subjectOptions) subjectOptions.style.display = 'block';
            } else {
                if (subjectOptions) subjectOptions.style.display = 'none';
            }
        };
        if (subject) {
            subject.addEventListener('change', handleSubjectChange);
        }

        // Form Submission
        const btnForm = container.querySelector('#btn_form') as HTMLElement;
        const formSend = container.querySelector('#formSend');
        
        const handleFormSubmit = async () => {
            if (!formSend) return;
            const formInputs = formSend.querySelectorAll('input,textarea,select') as NodeListOf<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
            let inputs: any[] = [];
            let hasError = false;

            formInputs.forEach(input => {
                input.style.border = '1px solid #e2e2e2';
            });

            for (let i = 0; i < formInputs.length; i++) {
                const input = formInputs[i];
                
                // Phone validation (index 1 in legacy code)
                if (i === 1 && input.value.length < 15) {
                    input.style.border = '1px solid #bd362f';
                    alert('Verifique os campos obrigatórios.');
                    hasError = true;
                    break;
                }

                if (input.value === '' && (input.parentElement?.style.display !== 'none')) {
                    input.style.border = '1px solid #bd362f';
                    alert('Verifique os campos obrigatórios.');
                    hasError = true;
                    break;
                }

                inputs.push({
                    name: input.getAttribute('name') || input.id,
                    value: input.value,
                });
            }

            if (hasError) return;

            try {
                setMainLoading(true);
                setMainLoadingIcon(true);
                setMainLoadingTitle(t('layout.confirm.loading'));

                const payload = new URLSearchParams();
                payload.append('form_id', String(pageId));
                inputs.forEach((input, index) => {
                    payload.append(`inputs[${index}][name]`, input.name);
                    payload.append(`inputs[${index}][value]`, input.value);
                });

                await companyService.processForm(payload.toString());
                
                setMainLoadingIcon(false);
                setMainLoadingIconSuccess(true);
                setMainLoadingTitle('Sucesso!');
                setMainLoadingMessage('Sua mensagem foi enviada com sucesso!');
                setMainLoadingCloseButton(true);

                setTimeout(() => {
                    setMainLoading(false);
                    navigate('/');
                }, 2000);

            } catch (error) {
                setMainLoadingIcon(false);
                setMainLoadingIconError(true);
                setMainLoadingTitle('Erro');
                setMainLoadingMessage('Ocorreu um erro ao enviar a mensagem!');
                setMainLoadingCloseButton(true);
            }
        };

        if (btnForm) {
            btnForm.addEventListener('click', handleFormSubmit);
        }

        return () => {
            if (cellPhoneInput) cellPhoneInput.removeEventListener('input', handlePhoneMask);
            if (formDataHora) formDataHora.removeEventListener('input', handleDateMask);
            if (subject) subject.removeEventListener('change', handleSubjectChange);
            if (btnForm) btnForm.removeEventListener('click', handleFormSubmit);
        };
    }, [htmlContent, companyService, navigate, pageId, setMainLoading, setMainLoadingIcon, setMainLoadingIconError, setMainLoadingIconSuccess, setMainLoadingMessage, setMainLoadingTitle, setMainLoadingCloseButton, t]);

    return (
        <DefaultLayout>
            <div className="w-full max-w-container mx-auto p-4 md:p-8 min-h-full">
                {loading ? (
                    <div className="flex gap-1 justify-center items-center py-20 h-full text-gray-500">
                        <ImSpinner8 className="animate-spin" /><span>{t('layout.loading')}</span>
                    </div>
                ) : (
                    <>
                        {!errorPage &&
                            <Breadcrumbs separator="›" aria-label="breadcrumb" className='text-sm bg-gray-50 p-2 px-4 rounded-full w-fit'>
                                <Link color="inherit" underline="none" href={getLinkToHome()}><TbHome /></Link>
                                <span className='font-semibold pointer-events-none'>{title}</span>
                            </Breadcrumbs>
                        }
                        <div
                            ref={containerRef}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlContent, {
                                USE_PROFILES: { html: true },
                                ADD_TAGS: ['style'],
                                ADD_ATTR: ['style', 'class', 'id'],
                            }) }} 
                            className="institutional-content h-full"
                        />
                    </>
                )}
            </div>
            {/* Debug info - hidden */}
            <div style={{ display: 'none' }}>Page: {page_slug} | ID: {pageId} | Content Length: {htmlContent?.length}</div>
        </DefaultLayout>
    );
};

export default Institutional;
